import { Tag } from '../../../@types';
import { TagCellItem } from '../../../components/molecules/TagCell';
import { AutocompleteTagType } from '../../../components/organisms/Tags';
import { defaultColors } from '../../../configs/colors';

/**
 * Fallback so a tag typed but not yet saved still renders as a chip. Takes the
 * first swatch of the tag colour picker rather than a colour of its own, so an
 * uncommitted chip already looks like the tag it is about to become.
 */
const UNSAVED_TAG_COLOUR = defaultColors[0];

/**
 * Maps the tag editor's working state onto the read-only chips in the summary
 * bar, so the bar tracks what the user has just typed rather than only what was
 * last saved.
 *
 * The editor holds plain strings for tags the user has typed but not yet
 * committed, which is why the union needs unpicking here.
 */
export const toSummaryTags = (
  selectedTags: (AutocompleteTagType | string)[] = [],
  newTags: Tag[] = []
): TagCellItem[] => {
  const selected = selectedTags.map((tag) =>
    typeof tag === 'string'
      ? { title: tag, color: UNSAVED_TAG_COLOUR }
      : { title: tag.label, color: tag.color || UNSAVED_TAG_COLOUR, isSystem: tag.isSystem }
  );

  // A brand-new tag is present in both lists while it is being created; keep the
  // first occurrence so it does not render as a duplicate chip.
  const seen = new Set(selected.map((tag) => tag.title));
  const created = newTags
    .filter((tag) => !seen.has(tag.text))
    .map((tag) => ({ title: tag.text, color: tag.colour || UNSAVED_TAG_COLOUR }));

  return [...selected, ...created].filter((tag) => !!tag.title);
};
